import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Timestamp, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { findValidUser, getUserFromTimeBasedCollection } from '../utils/firebaseUtils';
import messageReceiveImage from '../assets/images/message-receive.png';
import FirebaseErrorPopup from './FirebaseErrorPopup';
import { useFirebaseError } from '../hooks/useFirebaseError';

const MessageNotification = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isVisible' && prop !== 'isAnimating' && prop !== 'isExiting' && prop !== 'isImageReady'
})<{ isVisible: boolean; isAnimating: boolean; isExiting: boolean; isImageReady: boolean }>`
  position: fixed;
  top: ${props => {
    if (props.isExiting) return '-40dvh'; // 사라질 때는 위로 올라감
    if (props.isAnimating) return '2dvh'; // 애니메이션 중일 때는 위쪽에 위치
    return '-40dvh'; // 기본 상태는 화면 위쪽 밖 (더 가깝게)
  }};
  left: 50%;
  width: 90%;
  max-width: 400px;
  z-index: 999999;
  opacity: 1;
  transition: all 1.0s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transform: translateX(-50%) ${props => {
    if (props.isExiting) return 'translateY(-30px)'; // 사라질 때는 위로 이동
    if (props.isAnimating) return 'translateY(0)'; // 애니메이션 중일 때는 제자리
    return 'translateY(-20px)'; // 기본 상태는 위쪽으로 이동 (더 작게)
  }};
  filter: ${props => props.isAnimating && !props.isExiting && props.isImageReady ? 'blur(0px)' : 'blur(2px)'};
  -webkit-transform: translateX(-50%) ${props => {
    if (props.isExiting) return 'translateY(-30px)';
    if (props.isAnimating) return 'translateY(0)';
    return 'translateY(-20px)';
  }};
  -webkit-transition: all 1.0s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  pointer-events: none;
  will-change: transform, opacity;
  
  /* 모바일 브라우저 호환성을 위한 추가 속성 */
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  -webkit-perspective: 1000;
  perspective: 1000;
  
  /* iOS Safari 호환성을 위한 추가 속성 */
  -webkit-overflow-scrolling: touch;
  -webkit-tap-highlight-color: transparent;
  
  /* message-send 페이지에서도 제대로 표시되도록 추가 속성 */
  isolation: isolate;
  contain: layout style paint;
  
  /* message-send 페이지의 Container overflow 문제 해결 */
  position: fixed !important;
  z-index: 999999 !important;
  
  /* 모든 브라우저에서 확실하게 표시되도록 */
  display: block !important;
  visibility: visible !important;
`;

const MessageContainer = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MessageImage = styled.img.withConfig({
  shouldForwardProp: (prop) => prop !== 'isLoaded'
})<{ isLoaded: boolean }>`
  width: 100%;
  height: auto;
  object-fit: contain;
  opacity: ${props => props.isLoaded ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const MessageContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8%;
  box-sizing: border-box;
`;

const MessageText = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'textLength'
})<{ textLength: number }>`
  font-size: ${props => {
    const baseSize = 4.1;
    return props.textLength > 80 ? `${baseSize * 0.7}vw` : `${baseSize}vw`;
  }};
  color: white;
  text-align: center;
  font-family: 'MapoDPP', sans-serif;
  line-height: 1.4;
  word-break: keep-all;
  white-space: pre-line;
  text-shadow: 0 0 15px #3884FF;
  margin-top: 1.5vw;
`;

interface ReceivedMessage {
  message: string;
  senderNickname: string;
  receivedAt: Timestamp;
}

interface MessageReceiverProps {
  currentUserNickname?: string;
  currentUserAvatarNumber?: string;
}

type ListenerStatus = 'idle' | 'connecting' | 'connected' | 'error' | 'reconnecting';

const MessageReceiver: React.FC<MessageReceiverProps> = ({ 
  currentUserNickname, 
  currentUserAvatarNumber 
}) => {
  const [currentMessage, setCurrentMessage] = useState<ReceivedMessage | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isImageReady, setIsImageReady] = useState(false);
  const [listenerStatus, setListenerStatus] = useState<ListenerStatus>('idle');
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const receivedMessagesRef = useRef<ReceivedMessage[]>([]);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const componentMountTimeRef = useRef<number>(Date.now());
  const lastVisibilityChangeRef = useRef<number>(Date.now());
  const messageQueueRef = useRef<ReceivedMessage[]>([]);
  const isProcessingQueueRef = useRef<boolean>(false);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const retryCountRef = useRef<number>(0);
  const maxRetriesRef = useRef<number>(3);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const androidCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isImageReadyRef = useRef<boolean>(false);
  
  // Firebase 에러 핸들링 훅
  const { errorState, handleFirebaseError, hideError } = useFirebaseError();

  // 네트워크 상태 모니터링
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // 네트워크가 복구되면 리스너 재시작 (연결될 때까지 시도)
      retryCountRef.current = 0; // 재시도 카운트 리셋
      initializeListener();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setListenerStatus('error');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [listenerStatus]);

  // localStorage 변화 감지 (페이지 전환 시 사용자 정보 변화 감지)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'currentUser' && e.newValue) {
        // 사용자 정보가 변경되면 리스너 재시작
        setTimeout(() => {
          retryCountRef.current = 0; // 재시도 카운트 리셋
          initializeListener();
        }, 1000); // 1초 후 재시작 (페이지 전환 완료 대기)
      }
    };

    // 같은 탭에서의 localStorage 변화도 감지 (더 적극적으로)
    const handleStorageChangeLocal = () => {
      const currentUser = localStorage.getItem('currentUser');
      if (currentUser) {
        try {
          const userData = JSON.parse(currentUser);
          if (userData.nickname && userData.avatarNumber) {
            // 리스너가 없거나 연결되지 않은 상태라면 리스너 시작
            if (!unsubscribeRef.current || listenerStatus === 'error' || listenerStatus === 'idle') {
              // 이전 리스너 정리
              if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
              }
              
              // 즉시 리스너 시작
              startMessageListener(userData.nickname, userData.avatarNumber);
            }
          }
        } catch (error) {
          console.error('사용자 데이터 파싱 오류:', error);
        }
      }
    };

    // 커스텀 이벤트 리스너 추가 (ConstellationParty에서 발생시키는 이벤트)
    const handleMessageReceiverRestart = (e: CustomEvent) => {
      const { nickname, avatarNumber } = e.detail;
      if (nickname && avatarNumber) {
        // 이전 리스너 정리
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        // 즉시 리스너 시작
        startMessageListener(nickname, avatarNumber);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('messageReceiverRestart', handleMessageReceiverRestart as EventListener);
    
    // 더 자주 localStorage 상태 확인 (화신 등록 후 감지용)
    const storageCheckInterval = setInterval(handleStorageChangeLocal, 500); // 500ms마다 확인 (QR 접속 대응)
    
    // 컴포넌트 마운트 직후 즉시 한 번 확인 (화신 등록 직후 감지용)
    setTimeout(() => {
      handleStorageChangeLocal();
    }, 100); // 100ms로 단축 (QR 접속 대응)
    
    // 갤럭시폰에서 주기적으로 리스너 상태 확인 및 강제 재시작
    const isAndroid = /Android/.test(navigator.userAgent);
    if (isAndroid) {
      const androidHealthCheck = setInterval(() => {
        const currentUser = localStorage.getItem('currentUser');
        if (currentUser && (!unsubscribeRef.current || listenerStatus === 'error')) {
          try {
            const userData = JSON.parse(currentUser);
            if (userData.nickname && userData.avatarNumber) {
              startMessageListener(userData.nickname, userData.avatarNumber);
            }
          } catch (error) {
            console.error('Android 사용자 데이터 파싱 오류:', error);
          }
        }
      }, 2000); // 2초마다 확인 (더 자주)
      
      // 정리 함수에 추가
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('messageReceiverRestart', handleMessageReceiverRestart as EventListener);
        clearInterval(storageCheckInterval);
        clearInterval(androidHealthCheck);
      };
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('messageReceiverRestart', handleMessageReceiverRestart as EventListener);
      clearInterval(storageCheckInterval);
    };
  }, [listenerStatus]);

  // 이미지 프리로딩
  useEffect(() => {
    const preloadImage = () => {
      const img = new Image();
      img.onload = () => {
        setIsImageLoaded(true);
        setIsImageReady(true);
        isImageReadyRef.current = true;
      };
      img.onerror = () => {
        console.error('메시지 배경 이미지 로딩 실패');
        setIsImageLoaded(true); // 실패해도 팝업은 표시
        setIsImageReady(true); // 실패해도 애니메이션은 시작
        isImageReadyRef.current = true;
      };
      img.src = messageReceiveImage;
    };

    preloadImage();
  }, []);

  // 재연결 로직 (연결될 때까지 계속 시도)
  const retryConnection = () => {
    // 최대 재시도 횟수는 10회로 증가 (연결될 때까지 시도)
    if (retryCountRef.current >= 10) {
      setListenerStatus('error');
      
      // 30초 후 재시도 카운트 리셋하고 다시 시작
      setTimeout(() => {
        retryCountRef.current = 0;
        retryConnection();
      }, 30000);
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, retryCountRef.current), 10000);
    
    setListenerStatus('reconnecting');
    
    reconnectTimeoutRef.current = setTimeout(() => {
      retryCountRef.current++;
      initializeListener();
    }, delay);
  };

  // 놓친 메시지 체크 (개선된 버전)
  const checkForMissedMessages = async () => {
    try {
      // 현재 사용자 정보 가져오기
      const currentUser = localStorage.getItem('currentUser');
      if (!currentUser) {
        return;
      }

      const userData = JSON.parse(currentUser);
      const { nickname, avatarNumber } = userData;
      
      if (!nickname || !avatarNumber) {
        return;
      }

      // 사용자 위치 확인
      const userInfo = await getUserFromTimeBasedCollection(nickname, avatarNumber);
      if (!userInfo) {
        return;
      }

      const { collectionName, docId } = userInfo;
      const userDocRef = doc(db, collectionName, docId);
      
      // 일회성 읽기로 현재 메시지 상태 확인
      const { getDoc } = await import('firebase/firestore');
      const docSnapshot = await getDoc(userDocRef);
      
      if (docSnapshot.exists()) {
        const userData = docSnapshot.data();
        const messages = userData.receivedMessages || [];
        
        // 컴포넌트 마운트 시간 이후의 메시지만 확인
        const mountTime = componentMountTimeRef.current;
        
        // 새로운 메시지가 있는지 확인 (타임스탬프 기반으로 더 정확하게)
        const missedMessages = messages.filter((message: ReceivedMessage) => {
          // 컴포넌트 마운트 이후의 메시지이고
          const messageTime = message.receivedAt.toMillis();
          const isAfterMount = messageTime > mountTime;
          
          // 이미 받은 메시지가 아닌 경우
          const isNotReceived = !receivedMessagesRef.current.some(receivedMsg => 
            receivedMsg.message === message.message && 
            receivedMsg.senderNickname === message.senderNickname &&
            Math.abs(receivedMsg.receivedAt.toMillis() - messageTime) < 100 // 100ms 이내의 정확히 같은 메시지만 중복으로 간주
          );
          
          return isAfterMount && isNotReceived;
        });
        
        if (missedMessages.length > 0) {
          // 놓친 메시지들을 시간순으로 정렬해서 큐에 추가
          missedMessages
            .sort((a: ReceivedMessage, b: ReceivedMessage) => a.receivedAt.toMillis() - b.receivedAt.toMillis())
            .forEach((message: ReceivedMessage) => {
              addMessageToQueue(message);
            });
          
          // 받은 메시지 목록 업데이트
          receivedMessagesRef.current = messages;
        }
      }
    } catch (error) {
      console.error('놓친 메시지 체크 오류:', error);
    }
  };

  // 리스너 초기화 함수
  const initializeListener = () => {
    // 이전 리스너 정리 (사용자 변경 시 충돌 방지)
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    
    let nickname: string | undefined;
    let avatarNumber: string | undefined;

    if (!currentUserNickname || !currentUserAvatarNumber) {
      // 로컬 스토리지에서 사용자 정보 가져오기
      const currentUser = localStorage.getItem('currentUser');
      
      if (currentUser) {
        try {
          const userData = JSON.parse(currentUser);
          
          if (userData.nickname && userData.avatarNumber) {
            nickname = userData.nickname;
            avatarNumber = userData.avatarNumber;
          }
        } catch (error) {
          console.error('사용자 데이터 파싱 오류:', error);
        }
      }
    } else {
      nickname = currentUserNickname;
      avatarNumber = currentUserAvatarNumber;
    }

    if (nickname && avatarNumber) {
      startMessageListener(nickname, avatarNumber);
    } else {
      // 사용자 정보가 없으면 잠시 후 재시도 (연결될 때까지 계속 시도)
      if (!nickname || !avatarNumber) {
        setTimeout(() => {
          initializeListener();
        }, 1000); // 3초 → 1초로 변경 (더 빠른 재시도)
      }
    }
  };

  useEffect(() => {
    // 이전 리스너 정리 (여러 번 회원가입 시 충돌 방지)
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    
    // 이전 메시지 큐 정리
    messageQueueRef.current = [];
    receivedMessagesRef.current = [];
    isProcessingQueueRef.current = false;
    
    
    // 즉시 사용자 정보 확인 및 리스너 시작
    const startListenerImmediately = () => {
      const currentUser = localStorage.getItem('currentUser');
      if (currentUser) {
        try {
          const userData = JSON.parse(currentUser);
          
          if (userData.nickname && userData.avatarNumber) {
            startMessageListener(userData.nickname, userData.avatarNumber);
            return;
          }
        } catch (error) {
          console.error('사용자 데이터 파싱 오류:', error);
        }
      }
      
      // 사용자 정보가 없으면 더 자주 체크 (QR 접속 대기)
      setTimeout(startListenerImmediately, 1000); // 1초
    };
    
    // 즉시 시작
    startListenerImmediately();

    // 컴포넌트 언마운트 시 리스너 정리
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (androidCheckTimeoutRef.current) {
        clearTimeout(androidCheckTimeoutRef.current);
        androidCheckTimeoutRef.current = null;
      }
    };
  }, [currentUserNickname, currentUserAvatarNumber]);

  // 모바일 환경에서 페이지 가시성 변화 감지
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        lastVisibilityChangeRef.current = Date.now();
        
        // 페이지가 다시 활성화되면 리스너 재시작
        retryCountRef.current = 0; // 재시도 카운트 리셋
        
        // 사용자 정보가 있는지 확인 후 즉시 리스너 시작
        const currentUser = localStorage.getItem('currentUser');
        if (currentUser) {
          try {
            const userData = JSON.parse(currentUser);
            if (userData.nickname && userData.avatarNumber) {
              // 이전 리스너 정리
              if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
              }
              // 즉시 리스너 시작
              startMessageListener(userData.nickname, userData.avatarNumber);
              
              // 페이지 활성화 시 놓친 메시지 체크
              setTimeout(() => {
                checkForMissedMessages();
              }, 2000); // 2초 후 체크 (리스너 연결 대기)
            }
          } catch (error) {
            console.error('페이지 활성화 시 사용자 데이터 파싱 오류:', error);
          }
        }
      }
    };

    // 모바일 브라우저에서 페이지 포커스/블러 이벤트도 감지
    const handleFocus = () => {
      if (listenerStatus === 'error') {
        retryCountRef.current = 0;
        initializeListener();
      }
    };

    const handleBlur = () => {
      // 페이지 블러 시 특별한 처리 없음
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [listenerStatus]);

  const startMessageListener = (nickname: string, avatarNumber: string) => {
    // 기존 리스너가 있으면 정리
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    
    // 기존 재연결 타이머가 있으면 정리
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    setListenerStatus('connecting');

    // 새로운 시간 기반 컬렉션 구조를 사용하여 사용자 정보 가져오기
    getUserFromTimeBasedCollection(nickname, avatarNumber)
      .then((userInfo) => {
        if (!userInfo) {
          setListenerStatus('error');
          retryConnection();
          return;
        }

        const { collectionName, docId } = userInfo;

        // 해당 컬렉션의 문서에 실시간 리스너 설정
        const userDocRef = doc(db, collectionName, docId);
        
        const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
          // 연결 성공 시 상태 업데이트 (클로저 문제 해결)
          if (listenerStatus !== 'connected') {
            setListenerStatus('connected');
            retryCountRef.current = 0; // 성공 시 재시도 카운트 리셋
            
            // 연결 성공 시 놓친 메시지 체크
            setTimeout(() => {
              checkForMissedMessages();
            }, 1000); // 1초 후 체크 (연결 안정화 대기)
          }
          
          if (!docSnapshot.exists()) {
            return;
          }

          const userData = docSnapshot.data();
          const messages = userData.receivedMessages || [];
          
          // 새로운 메시지 감지 로직 (더 정확한 버전)
          if (messages.length > 0) {
            // 첫 번째 로드인 경우 (receivedMessagesRef가 비어있음)
            if (receivedMessagesRef.current.length === 0) {
              receivedMessagesRef.current = messages;
              return;
            }
            
            // 메시지 개수가 증가했는지 확인
            if (messages.length > receivedMessagesRef.current.length) {
              // 새로 추가된 메시지들만 필터링 (개수 기반)
              const newMessages = messages.slice(receivedMessagesRef.current.length);
              
              // 각 새 메시지를 큐에 추가
              newMessages.forEach((message: ReceivedMessage) => {
                addMessageToQueue(message);
              });
            } else if (messages.length === receivedMessagesRef.current.length) {
              // 메시지 개수는 같지만 내용이 바뀌었을 수 있음 (매우 드문 경우)
              const mountTime = componentMountTimeRef.current;
              const newMessages = messages.filter((message: ReceivedMessage) => {
                const messageTime = message.receivedAt.toMillis();
                const isAfterMount = messageTime > mountTime;
                
                // 이미 처리된 메시지가 아닌지 확인
                const isNotProcessed = !receivedMessagesRef.current.some(receivedMsg => 
                  receivedMsg.message === message.message && 
                  receivedMsg.senderNickname === message.senderNickname &&
                  Math.abs(receivedMsg.receivedAt.toMillis() - messageTime) < 100
                );
                
                return isAfterMount && isNotProcessed;
              });
              
              if (newMessages.length > 0) {
                newMessages.forEach((message: ReceivedMessage) => {
                  addMessageToQueue(message);
                });
              }
            }
            
            receivedMessagesRef.current = messages;
          }
        }, (error) => {
          console.error('MessageReceiver: 메시지 리스너 오류:', error);
          setListenerStatus('error');
          
          // Firebase 에러 팝업 표시
          handleFirebaseError(error, '메시지를 불러오는 중 오류가 발생했습니다.');
          
          // 특정 에러 코드에 따른 처리
          if (error.code === 'unavailable' || error.code === 'deadline-exceeded') {
            retryConnection();
          } else if (error.code === 'permission-denied') {
            // 권한 거부 - 재시도하지 않음
          } else {
            retryConnection();
          }
        });

        unsubscribeRef.current = unsubscribe;
      })
      .catch((error) => {
        console.error('MessageReceiver: 사용자 정보 가져오기 오류:', error);
        setListenerStatus('error');
        // Firebase 에러 팝업 표시
        handleFirebaseError(error, '사용자 정보를 불러오는 중 오류가 발생했습니다.');
        retryConnection();
      });
  };

  const processMessageQueue = () => {
    // 이미 처리 중이면 큐에만 추가하고 리턴
    if (isProcessingQueueRef.current) {
      return;
    }

    // 큐가 비어있으면 처리 상태 해제하고 리턴
    if (messageQueueRef.current.length === 0) {
      isProcessingQueueRef.current = false;
      return;
    }

    // 처리 시작
    isProcessingQueueRef.current = true;
    const message = messageQueueRef.current.shift();
    
    if (message) {
      // 메시지 표시
      setCurrentMessage(message);
      setIsExiting(false);
      setIsVisible(true);
      
      // 애니메이션 시작
      setTimeout(() => {
        setIsAnimating(true);
      }, 50);

      // 5초 후 메시지 숨기기
      setTimeout(() => {
        setIsExiting(true);
        
        // 1초 후 완전히 숨기고 다음 메시지 처리
        setTimeout(() => {
          setIsAnimating(false);
          setIsExiting(false);
          setCurrentMessage(null);
          isProcessingQueueRef.current = false;
          
          // 큐에 더 많은 메시지가 있으면 다음 메시지 처리
          if (messageQueueRef.current.length > 0) {
            processMessageQueue();
          }
        }, 1000);
      }, 5000);
    } else {
      isProcessingQueueRef.current = false;
    }
  };

  const addMessageToQueue = (message: ReceivedMessage) => {
    // 중복 메시지 방지
    const isDuplicate = messageQueueRef.current.some(existingMessage => 
      existingMessage.message === message.message && 
      existingMessage.senderNickname === message.senderNickname &&
      Math.abs(existingMessage.receivedAt.toMillis() - message.receivedAt.toMillis()) < 100
    );
    
    if (isDuplicate) {
      return;
    }
    
    // 큐에 메시지 추가
    messageQueueRef.current.push(message);
    
    // 처리 중이 아니면 즉시 처리 시작
    if (!isProcessingQueueRef.current) {
      processMessageQueue();
    }
  };

  const handleImageLoad = () => {
    setIsImageLoaded(true);
  };

  const showMessage = (message: ReceivedMessage) => {
    // 단순화된 메시지 표시 (processMessageQueue에서 직접 처리)
  };

  const startBannerAnimation = () => {
    // 단순화된 애니메이션 (processMessageQueue에서 직접 처리)
  };


  if (!currentMessage) {
    return null;
  }

  // 메시지 형식: [성좌 '보낸사람 nickname'이(가) {선택한메시지}]
  const formattedMessage = `[성좌 '${currentMessage.senderNickname}'이(가)\n${currentMessage.message}]`;

  return (
    <>
      <MessageNotification 
        isVisible={isVisible} 
        isAnimating={isAnimating} 
        isExiting={isExiting} 
        isImageReady={isImageReady}
        data-message-receiver="true"
      >
        <MessageContainer>
          <MessageImage 
            src={messageReceiveImage} 
            alt="메시지 수신" 
            isLoaded={isImageLoaded}
            ref={imageRef}
            onLoad={handleImageLoad}
          />
          <MessageContent>
            <MessageText textLength={formattedMessage.length}>
              {formattedMessage}
            </MessageText>
          </MessageContent>
        </MessageContainer>
      </MessageNotification>
      
      {/* Firebase 에러 팝업 */}
      <FirebaseErrorPopup
        isVisible={errorState.isError}
        onClose={hideError}
        message={errorState.errorMessage}
      />
    </>
  );
};

export default MessageReceiver;
